import { z } from 'zod';
import { localeCodeSchema } from './i18n.js';
import { partnerSetupRoleSchema } from './partner.js';
import { tenantSlugSchema } from './tenant.js';
import { platformRoleSchema } from './user.js';
/**
 * The authentication contract.
 *
 * Deliberately says nothing about *how* a principal is authenticated. The API verifies
 * an RS256 JWT against a JWKS endpoint and reads its claims; whether that token was
 * minted by the local identity adapter or by Cognito is a configuration detail the
 * client never observes. See `apps/api/src/modules/platform/identity/port.ts`.
 */
/** Minutes, not hours: a short access-token life keeps revocation cheap while there is no refresh flow. */
export const ACCESS_TOKEN_TTL_SECONDS = 3600;
export const loginRequestSchema = z.object({
    email: z.string().email().max(320),
    password: z.string().min(1).max(512),
    /**
     * Required only when the user belongs to more than one tenant. A token is always
     * scoped to exactly one tenant, because `tenantId` is a claim and a claim cannot be
     * ambiguous. Omitting it with multiple memberships yields `TENANT_REQUIRED`.
     */
    tenantSlug: tenantSlugSchema.optional(),
});
export const loginResponseSchema = z.object({
    accessToken: z.string().min(1),
    tokenType: z.literal('Bearer'),
    expiresIn: z.number().int().positive(),
});
/**
 * The claim names the API reads off a verified token.
 *
 * A token is scoped to exactly one tenant, or — adr/0035 — to none, for a partner principal.
 * The two shapes are discriminated by `principalKind`; the adapter decides which one a
 * verified payload is (no tenant claim under any name → `partner`), never the middleware.
 *
 * `tenant_id` is a custom claim. Cognito namespaces custom claims as `custom:tenant_id`,
 * so the mapping from raw claim to this shape is the adapter's job — never the
 * middleware's. That is precisely what keeps the swap surgical.
 *
 * `email` is NOT validated as an email format. A Cognito ACCESS token never carries an
 * `email` claim at all — the Cognito adapter falls back to `username`, which for this
 * pool (email as sign-in alias, not as the Username attribute) is Cognito's own
 * auto-generated UUID subject, not the address. Nothing downstream of `toTokenClaims`
 * reads this field (`buildAuthContext` in `apps/api/src/lib/authenticate.ts` never
 * touches `claims.email`), so requiring email syntax here only rejected every real
 * Cognito-authenticated request as `TOKEN_INVALID`.
 */
const tokenClaimsBase = {
    sub: z.string().min(1),
    email: z.string().min(1),
    tokenUse: z.literal('access'),
};
export const tenantTokenClaimsSchema = z.object({
    principalKind: z.literal('tenant'),
    tenantId: z.string().min(1),
    ...tokenClaimsBase,
});
/** adr/0035: a partner principal's token carries no tenant claim; partnerId is resolved per request, never claimed. */
export const partnerTokenClaimsSchema = z.object({
    principalKind: z.literal('partner'),
    ...tokenClaimsBase,
});
export const tokenClaimsSchema = z.discriminatedUnion('principalKind', [
    tenantTokenClaimsSchema,
    partnerTokenClaimsSchema,
]);
/**
 * One role grant, as the caller sees their own. S2.
 *
 * `scopeType` mirrors the Prisma `ScopeType` enum and `SCOPE_TYPES` in `@instanexus/authz`;
 * all three must stay identical in members and order. It is spelled literally here rather
 * than imported because `contracts` is dependency-free and nothing may reverse the
 * `authz → contracts` direction of that dependency.
 */
export const roleAssignmentDTOSchema = z.object({
    roleKey: z.string().min(1),
    scopeType: z.enum(['TENANT', 'LOCATION']),
    /** Null when `scopeType` is `TENANT`. A location id when it is `LOCATION`. */
    scopeId: z.string().min(1).nullable(),
});
/**
 * adr/0035 decision 4. Present on `me` ONLY when the caller is a partner's setup session inside
 * this tenant; drives the console's "configuring as <partner>" banner. Courtesy, never the
 * boundary — the server's setup-role check is what refuses.
 */
export const meSetupSessionSchema = z.object({
    partnerId: z.string().min(1),
    partnerName: z.string().min(1),
    roleKey: partnerSetupRoleSchema,
    expiresAt: z.string().datetime(),
});
/** The employee a login is (Person.userId link, adr/0036 decision 4). No email, phone or userId (S7). */
export const mePersonDTOSchema = z
    .object({
    id: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    personType: z.enum(['EMPLOYEE', 'CONTRACTOR']),
})
    .strict();
/** `GET /api/web/auth/me` — who the caller is, and which tenant this token is scoped to. */
export const meDTOSchema = z.object({
    userId: z.string().min(1),
    email: z.string().email(),
    name: z.string().min(1),
    preferredLocale: localeCodeSchema,
    isPlatformOperator: z.boolean(),
    platformRole: platformRoleSchema.nullable(),
    tenantId: z.string().min(1),
    tenantSlug: z.string().min(1),
    tenantName: z.string().min(1),
    /**
     * S2. The caller's own grants in this tenant, and the permission strings those grants
     * resolve to. Both are derived server-side by `@instanexus/authz` — the client never
     * computes authorization, it only renders what it was told. A UI that hides a control
     * the server would refuse is a courtesy; the server refusing it is the security boundary
     * (docs/005 §Enforcement).
     *
     * `permissions` is `string[]` rather than the `Permission` union because `contracts` may
     * not import `authz`. Sorted, de-duplicated.
     */
    roles: z.array(roleAssignmentDTOSchema),
    permissions: z.array(z.string().min(1)),
    /**
     * S26 Phase 4. The tenant's trial has ended and no plan has been chosen, so every route
     * except `/auth/*` and `/billing/*` answers 402 `PLAN_SELECTION_REQUIRED`.
     *
     * ON `me` AND NOT ONLY ON THE 402, DELIBERATELY. The server-side gate is the boundary and is
     * what actually refuses; this flag is what lets the SPA render the right screen FIRST
     * instead of discovering the wall by crashing a query into it and interpreting the wreckage.
     * Both exist on purpose: a client that trusted this flag alone would be a client-side
     * paywall, and a client that had only the 402 would flash a broken page before redirecting.
     *
     * `false` for a platform operator with no tenant claim, and `false` for every tenant that
     * has never had a trial — see `billing/service.ts#isPlanSelectionRequired`.
     */
    planSelectionRequired: z.boolean(),
    /**
     * The tenant has not yet done the minimum that makes the product work, so the console
     * sends it through the first-run onboarding wizard instead of its normal landing page.
     *
     * DERIVED ON EVERY REQUEST, NEVER PERSISTED, and that is the whole design. There is no
     * "onboarding complete" column and no migration behind this: the server asks each
     * registered readiness check (`tenant/readiness.ts`) whether the thing it owns exists —
     * today, at least one `Location` and at least one active check-in flow — and this is the
     * negation of "every check passed". An abandoned wizard therefore leaves NOTHING behind:
     * the next `/auth/me` simply re-gates, and a tenant that later deletes its last location
     * is walked through setup again rather than left with a product that cannot check anyone
     * in. A stored flag would have to be repaired by hand in both directions.
     *
     * Same courtesy-not-boundary rule as `planSelectionRequired` above: this is what lets the
     * SPA render the wizard FIRST rather than discovering an empty product by rendering it.
     * Unlike the plan wall there is no matching server-side refusal, and there must not be —
     * an un-onboarded tenant is unconfigured, not unauthorized, and every route it can reach
     * is one it is entitled to reach.
     */
    onboardingRequired: z.boolean(),
    setupSession: meSetupSessionSchema.optional(),
    /**
     * The employee this login is (Person.userId link, adr/0036 decision 4), or null. App-neutral: web
     * and mobile read the same field. OPTIONAL ON THE WIRE so existing MeDTO fixtures and an older SPA
     * bundle stay valid; the server always sends it.
     */
    person: mePersonDTOSchema.nullable().optional(),
    /**
     * The `TERMS_VERSION` this user last accepted, or null (legacy / never). OPTIONAL ON THE WIRE so
     * older fixtures stay valid; the server always sends it. Absent means "unknown" and never prompts.
     */
    termsVersion: z.string().nullable().optional(),
});
//# sourceMappingURL=auth.js.map